import { api, withUserContext } from './Api';
import {
  PaymentType,
  PaymentPayloadType,
  PaymentExecuteResponseType,
} from '@/types/payment.type';
import { ParticipantType } from '@/types/ParticipantType';
import { BillType, BillsRequestType } from '@/types/BillType';
import { AxiosResponse } from 'axios';

// ===== STANDARDIZED PAYMENT SERVICE FUNCTIONS =====

/**
 * GET /v5/payments/{paymentId} - Exibição de Dados, Recibo e Carregamento de Pagamento
 */
async function getPayment(
  paymentId: string,
  userClientId?: string
): Promise<PaymentType> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .get<PaymentType>(`/v5/payments/${paymentId}`, config)
    .then((res: AxiosResponse<PaymentType>) => {
      return res.data;
    })
    .catch((error: any) => {
      throw error;
    });
}

/**
 * POST /v5/payments - Criação de Pagamento
 */
async function createPayment(
  payload: PaymentPayloadType,
  userClientId?: string
): Promise<PaymentType> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .post<PaymentType>('/v5/payments', payload, config)
    .then((res: AxiosResponse<PaymentType>) => {
      return res.data;
    })
    .catch((error: any) => {
      throw error;
    });
}

/**
 * PUT /v5/payments/{paymentId} - Atualização de Pagamento
 */
async function updatePayment(
  paymentId: string,
  payload: PaymentPayloadType,
  userClientId?: string
): Promise<PaymentType> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .put<PaymentType>(`/v5/payments/${paymentId}`, payload, config)
    .then((res: AxiosResponse<PaymentType>) => {
      return res.data;
    })
    .catch((error: any) => {
      throw error;
    });
}

/**
 * PATCH /v5/payments/{paymentId} - Execução de Pagamento
 */
async function executePayment(
  paymentId: string,
  userClientId?: string
): Promise<PaymentExecuteResponseType> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .patch<PaymentExecuteResponseType>(
      `/v5/payments/${paymentId}`,
      {}, // Body vazio sempre
      config
    )
    .then((res: AxiosResponse<PaymentExecuteResponseType>) => {
      return res.data;
    })
    .catch((error: any) => {
      throw error;
    });
}

/**
 * GET /v5/payments/options - Busca Bancos
 */
async function getOptions(userClientId?: string): Promise<ParticipantType[]> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .get<{ participants: ParticipantType[] }>('/v5/payments/options', config)
    .then((res: AxiosResponse<{ participants: ParticipantType[] }>) => {
      const participants = res.data?.participants || [];
      return participants;
    })
    .catch((error: any) => {
      throw error;
    });
}

/**
 * POST /v5/payments/{userTag}/bills - Busca Boletos
 */
async function getBills(
  userTag: string,
  request: BillsRequestType,
  userClientId?: string
): Promise<BillType[]> {
  const config = userClientId ? withUserContext(userClientId) : {};

  return await api
    .post<BillType[]>(`/v5/payments/${userTag}/bills`, request, config)
    .then((res: AxiosResponse<BillType[]>) => {
      return res.data;
    })
    .catch((error: any) => {
      throw error;
    });
}

// ===== BACKWARD COMPATIBILITY ALIASES =====

/**
 * @deprecated Use getPayment instead
 */
async function getById(
  paymentId: string,
  userClientId?: string
): Promise<PaymentType> {
  return getPayment(paymentId, userClientId);
}

/**
 * @deprecated Use createPayment instead
 */
async function create(
  data: PaymentPayloadType,
  userClientId?: string
): Promise<PaymentType> {
  return createPayment(data, userClientId);
}

/**
 * @deprecated Use updatePayment instead
 */
async function update(
  paymentId: string,
  data: Partial<PaymentType>,
  userClientId?: string
): Promise<PaymentType> {
  // Convert partial PaymentType to PaymentPayloadType for the API
  const payload = data.payload || (data as any);
  return updatePayment(paymentId, payload, userClientId);
}

/**
 * @deprecated Function not needed - createPaymentLink endpoint not in use
 */
async function createPaymentLink(data: {
  amount: number;
  description: string;
  userClientId?: string;
}): Promise<{ link: string; paymentId: string }> {
  const config = data.userClientId ? withUserContext(data.userClientId) : {};

  return await api
    .post<{ link: string; paymentId: string }>(
      '/v5/payments/create-link',
      data,
      config
    )
    .then((res: AxiosResponse<{ link: string; paymentId: string }>) => res.data)
    .catch((error: any) => {
      throw error;
    });
}

// ===== EXPORT SERVICE =====

export const PaymentsService = {
  // Primary standardized functions
  getPayment,
  createPayment,
  updatePayment,
  executePayment,
  getOptions,
  getBills,

  // Backward compatibility (deprecated)
  getById,
  create,
  update,
  createPaymentLink,
};
